import { changesIn, diffText, type Explanation } from '@lyrmo/shared';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Button } from '../../components/Button';
import { useI18n } from '../../i18n/i18n-context';
import { DiffView } from './DiffView';
import type { Explain } from './useExplanations';
import { readableChanges, type ReadableChange } from './version-changes';
import './explain.css';

type ExplainState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly explanations: ReadonlyMap<number, Explanation> }
  | { readonly status: 'failed'; readonly message: string };

interface ExplainViewProps {
  readonly original: string;
  readonly rewritten: string;
  readonly explain: Explain;
}

const ready = (explanations: readonly Explanation[]): ExplainState => ({
  status: 'ready',
  explanations: new Map(explanations.map((explanation) => [explanation.change, explanation])),
});

function ChangeSummary({ change }: { readonly change: ReadableChange }) {
  const { t } = useI18n();
  if (!change.before) return <ins>{change.after}</ins>;
  if (!change.after) return <del>{change.before}</del>;
  return (
    <>
      <del>{change.before}</del>
      <span className="explain-arrow" aria-label={t.explain.became}>
        →
      </span>
      <ins>{change.after}</ins>
    </>
  );
}

/**
 * The diff with each change numbered, and why each one helps: its kind and a one-line reason.
 * Changes are grouped here and explained by the API, matched back by number.
 */
export function ExplainView({ original, rewritten, explain }: ExplainViewProps) {
  const { t, describeError } = useI18n();
  const segments = useMemo(() => diffText(original, rewritten), [original, rewritten]);
  const changes = useMemo(() => changesIn(segments), [segments]);
  const readable = useMemo(
    () => readableChanges(original, rewritten, segments, changes),
    [changes, original, rewritten, segments],
  );
  // Explained before, as when coming back to this view, the reasons show at once.
  const [state, setState] = useState<ExplainState>(() => {
    const known = explain.known?.({ original, rewrite: rewritten });
    return known ? ready(known) : { status: 'loading' };
  });
  const [attempt, setAttempt] = useState(0);
  const loading = state.status === 'loading';

  useEffect(() => {
    if (changes.length === 0) return;
    let current = true;
    explain({
      original,
      rewrite: rewritten,
      changes: changes.map(({ before, after }) => ({ before, after })),
    }).then(
      (explanations) => {
        if (current) setState(ready(explanations));
      },
      (error: unknown) => {
        if (current) setState({ status: 'failed', message: describeError(error) });
      },
    );
    return () => {
      current = false;
    };
  }, [attempt, changes, describeError, explain, original, rewritten]);

  if (changes.length === 0) {
    return <p className="explain-empty">{t.explain.unchanged}</p>;
  }

  const marks = new Map(changes.map((change, index) => [change.last, index + 1]));
  return (
    <>
      <div className="explain-diff rich-text">
        <DiffView original={original} rewritten={rewritten} marks={marks} />
      </div>
      <ol className="explain-list" aria-label={t.card.whyEachChange} aria-busy={loading}>
        {changes.map((change, index) => {
          const explanation = state.status === 'ready' ? state.explanations.get(index) : undefined;
          return (
            <li
              key={`${String(change.first)}-${String(change.last)}`}
              className="explain-item"
              style={{ '--change': index } as CSSProperties}
            >
              <span className="explain-number" aria-hidden="true">
                {index + 1}
              </span>
              <div className="explain-body">
                <p className="explain-change">
                  <ChangeSummary change={readable[index] ?? change} />
                </p>
                {/* The reason, where a skeleton of it held its place while it was on its way. */}
                {(loading || explanation) && (
                  <div className="explain-slot">
                    <span className="explain-skeleton" aria-hidden="true">
                      <span className="explain-skeleton-kind" />
                      <span className="explain-skeleton-line" />
                    </span>
                    {explanation && (
                      <p className="explain-reason">
                        <span className="explain-kind" data-kind={explanation.kind}>
                          {t.explain.kinds[explanation.kind]}
                        </span>{' '}
                        {explanation.reason}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {state.status === 'failed' && (
        <div className="explain-failed" role="alert">
          <p>{state.message}</p>
          <Button
            icon="retry"
            onClick={() => {
              setState({ status: 'loading' });
              setAttempt((count) => count + 1);
            }}
          >
            {t.common.tryAgain}
          </Button>
        </div>
      )}
    </>
  );
}
